import {
  ApiError,
  api,
  type RecordFlight,
  type TechlogAircraft,
  type TechlogContext,
  type TechlogEntry,
} from '../api';
import { hoursFormat, hoursInputAttrs, hoursPlaceholder, hoursValue, parseHours } from '../hours';
import { errorCard, esc, fmtDate, fmtHours, fmtInt, html, loading, pill, toast } from '../ui';

const time = (iso: string | null): string => (iso ? new Date(iso).toISOString().slice(11, 16) : '—');
const today = (): string => new Date().toISOString().slice(0, 10);

/** A per-tab idempotency key, so a double submit files one flight, not two. */
const newEntryKey = (): string =>
  `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

// ── recent entries ──────────────────────────────────────────────────────────

function logCard(rows: TechlogEntry[]): string {
  return html`
    <div class="card">
      <div class="card-h">
        <div>
          <h2>Recent flights — all aircraft</h2>
          <p class="tiny dim">Newest first · signed entries cannot be edited, only amended</p>
        </div>
        <span class="lbl">${fmtInt(rows.length)} entries</span>
      </div>
      <div class="scrollx">
        <table class="tbl">
          <thead>
            <tr>
              <th>Aircraft</th><th>Date</th><th class="col-opt">Sector</th><th>Commander</th>
              <th class="col-opt">Off / on</th><th>Block</th><th>Ldg</th>
              <th class="col-opt">Fuel / oil</th><th>Defect</th>
            </tr>
          </thead>
          <tbody>
            ${rows.length === 0
              ? html`<tr><td colspan="9" class="muted">No flights recorded yet.</td></tr>`
              : rows
                  .map(
                    (f) => html`<tr class="click" data-aircraft="${esc(f.aircraftId)}">
                      <td class="st ${f.defect?.severity === 'GROUNDING' ? 's-exceed' : 's-none'} n">
                        <span class="reg">${esc(f.registration)}</span>
                      </td>
                      <td class="n">${esc(fmtDate(f.flightDate))}</td>
                      <td class="n col-opt">${esc(f.departure ?? '—')} → ${esc(f.arrival ?? '—')}</td>
                      <td>${esc(f.picName ?? '—')}</td>
                      <td class="n col-opt">${esc(time(f.offBlocks))} / ${esc(time(f.onBlocks))}</td>
                      <td class="n">${fmtHours(f.blockHours)} h</td>
                      <td class="n">${f.landings}</td>
                      <td class="n col-opt">
                        ${f.fuelUpliftL !== null ? `${f.fuelUpliftL.toFixed(0)} L` : '—'} /
                        ${f.oilUpliftL !== null ? `${f.oilUpliftL.toFixed(1)} L` : '—'}
                      </td>
                      <td class="tiny">
                        ${f.defect
                          ? pill(f.defect.severity === 'GROUNDING' ? 'OVERDUE' : 'DUE_SOON', f.defect.reference)
                          : '<span class="dim">—</span>'}
                      </td>
                    </tr>`,
                  )
                  .join('')}
          </tbody>
        </table>
      </div>
      <div class="card-f">
        Oil uplift per flight hour is trended per engine — a rising trend raises a flag on the
        reliability screen before it becomes a defect.
      </div>
    </div>
  `;
}

// ── capture form ────────────────────────────────────────────────────────────

function captureCard(ctx: TechlogContext): string {
  const flyable = ctx.aircraft.filter((a) => a.status !== 'GROUNDED');
  const first = flyable[0] ?? ctx.aircraft[0];

  return html`
    <div class="card">
      <div class="card-h">
        <h2>Flight capture</h2>
        <span class="lbl">phone &amp; tablet</span>
      </div>
      <div class="card-b" style="display:flex;justify-content:center;background:var(--panel-2)">
        <div class="phone">
          <div class="phone-top">
            <span id="cap-clock">${esc(new Date().toISOString().slice(11, 16))}</span>
            <span id="cap-state">READY</span>
          </div>
          <form class="phone-b form" id="capture" novalidate>
            <div class="field">
              <label for="cap-ac">Aircraft</label>
              <select id="cap-ac" name="aircraftId">
                ${ctx.aircraft
                  .map(
                    (a) => html`<option value="${esc(a.id)}" ${a.id === first?.id ? 'selected' : ''} ${a.status === 'GROUNDED' ? 'disabled' : ''}>
                      ${esc(a.registration)} — ${esc(a.typeModel)}${a.status === 'GROUNDED' ? ' (grounded)' : ''}
                    </option>`,
                  )
                  .join('')}
              </select>
            </div>

            <div id="cap-ac-note"></div>

            <div class="field">
              <label for="cap-date">Date</label>
              <input id="cap-date" name="flightDate" type="date" value="${today()}" />
            </div>

            <div class="frow">
              <div class="field"><label for="cap-from">From</label><input id="cap-from" name="departure" value="LRBS" style="text-transform:uppercase;font-family:var(--mono)" /></div>
              <div class="field"><label for="cap-to">To</label><input id="cap-to" name="arrival" value="LRBS" style="text-transform:uppercase;font-family:var(--mono)" /></div>
            </div>

            <div class="frow">
              <div class="field"><label for="cap-off">Off blocks</label><input id="cap-off" name="offBlocks" type="time" value="09:00" /></div>
              <div class="field"><label for="cap-on">On blocks</label><input id="cap-on" name="onBlocks" type="time" value="10:30" /></div>
            </div>

            <div class="field mono">
              <label for="cap-block">Block time (hours)</label>
              <input id="cap-block" name="blockHours" ${hoursInputAttrs(hoursValue(1.5))} value="${hoursValue(1.5)}" />
              <span class="hint">Computed from off/on blocks; override if the times were not recorded.</span>
            </div>

            <div class="frow">
              <div class="field mono"><label for="cap-ldg">Landings</label><input id="cap-ldg" name="landings" inputmode="numeric" value="1" style="text-align:right" /></div>
              <div class="field mono"><label for="cap-fuel">Fuel (L)</label><input id="cap-fuel" name="fuelUpliftL" inputmode="decimal" value="" style="text-align:right" /></div>
            </div>

            <div class="frow">
              <div class="field mono"><label for="cap-oil">Oil (L)</label><input id="cap-oil" name="oilUpliftL" inputmode="decimal" value="" style="text-align:right" /></div>
              <div class="field">
                <label for="cap-nature">Nature</label>
                <select id="cap-nature" name="natureOfFlight">
                  <option value="TRAINING">Training</option>
                  <option value="PRIVATE">Private</option>
                  <option value="FERRY">Ferry</option>
                  <option value="MAINTENANCE_CHECK">Maintenance check</option>
                </select>
              </div>
            </div>

            <div class="calc" id="cap-calc"></div>

            <label class="check"><input type="checkbox" id="cap-hasdefect" /> A defect was found on this flight</label>

            <div id="cap-defect" style="display:none" class="fset">
              <div class="lg">Defect</div>
              <div class="field">
                <label for="cap-defdesc">What was found</label>
                <textarea id="cap-defdesc" rows="2" placeholder="Describe it as observed, not the suspected cause"></textarea>
              </div>
              <div class="frow">
                <div class="field">
                  <label for="cap-defsev">Severity</label>
                  <select id="cap-defsev">
                    <option value="DEFERRABLE">Deferrable — MEL</option>
                    <option value="GROUNDING">Grounding — no flight</option>
                    <option value="ADVISORY">Advisory — monitor</option>
                  </select>
                </div>
                <div class="field" id="cap-melwrap">
                  <label for="cap-melcat">MEL category</label>
                  <select id="cap-melcat">
                    <option value="B">B — 3 days</option>
                    <option value="C">C — 10 days</option>
                    <option value="D">D — 120 days</option>
                    <option value="A">A — as stated</option>
                  </select>
                </div>
              </div>
              <div id="cap-defnote"></div>
            </div>

            <div id="cap-error"></div>

            <button class="btn pri wide" type="submit" id="cap-submit">Sign &amp; file entry</button>
            <p class="tiny dim" style="text-align:center">
              Signing records your identity, the device and the timestamp. A filed entry cannot be
              edited — a correction creates an amendment and keeps the original.
            </p>
          </form>
        </div>
      </div>
      <div class="card-f" id="cap-footer">
        Filing advances the aircraft counters and recomputes its due list in the same transaction.
      </div>
    </div>
  `;
}

function bindCapture(root: HTMLElement, ctx: TechlogContext, onFiled: () => void): void {
  const form = root.querySelector<HTMLFormElement>('#capture');
  if (!form) return;

  const $ = <T extends HTMLElement>(sel: string) => root.querySelector<T>(sel)!;
  const acSelect = $<HTMLSelectElement>('#cap-ac');
  const submit = $<HTMLButtonElement>('#cap-submit');
  let entryKey = newEntryKey();

  const current = (): TechlogAircraft | undefined =>
    ctx.aircraft.find((a) => a.id === acSelect.value);

  const recalcBlock = () => {
    const off = $<HTMLInputElement>('#cap-off').value;
    const on = $<HTMLInputElement>('#cap-on').value;
    if (!/^\d{2}:\d{2}$/.test(off) || !/^\d{2}:\d{2}$/.test(on)) return;
    const [oh, om] = off.split(':').map(Number);
    const [nh, nm] = on.split(':').map(Number);
    let mins = nh * 60 + nm - (oh * 60 + om);
    if (mins < 0) mins += 24 * 60;            // crossed midnight
    /**
     * Written in whichever convention this organisation keeps.
     *
     * Under hours and minutes it is written exactly — 09:00 to 10:05 is 1:05,
     * and rounding that to tenths would put 1:06 in a box computed from two
     * times that plainly say otherwise. Under decimal it keeps the tenth it
     * has always been rounded to, which is how a Hobbs meter reads and how
     * anybody working in decimals writes a block time down.
     */
    $<HTMLInputElement>('#cap-block').value =
      hoursFormat() === 'HOURS_MINUTES'
        ? hoursValue(mins / 60)
        : (Math.round((mins / 60) * 10) / 10).toFixed(1);
  };

  const refresh = () => {
    const a = current();
    const note = $('#cap-ac-note');
    const calc = $('#cap-calc');

    if (!a) return;

    note.innerHTML =
      a.status === 'GROUNDED'
        ? html`<div class="callout c-exceed"><span class="cg">✕</span><div>
            <b>${esc(a.registration)} is grounded.</b> ${esc(a.groundedReason ?? '')}
            No entry can be filed until the item is cleared.
          </div></div>`
        : '';

    const block = parseHours($<HTMLInputElement>('#cap-block').value) ?? 0;
    const ldg = Number($<HTMLInputElement>('#cap-ldg').value) || 0;
    const after = a.tsnHours + block;
    const remainingAfter = a.nextHourTask ? a.nextHourTask.remaining - block : null;

    calc.innerHTML = html`
      <div><span class="lbl">Block time</span><b>${fmtHours(block)} h</b></div>
      <div><span class="lbl">${esc(a.registration)} after filing</span><b>${fmtHours(after)} h</b></div>
      <div><span class="lbl">Landings total</span><b>${fmtInt(a.landingsTotal + ldg)}</b></div>
      ${a.nextHourTask
        ? html`<div>
            <span class="lbl">${esc(a.nextHourTask.title.slice(0, 26))}</span>
            <b style="color:var(--${(remainingAfter ?? 0) < 0 ? 'exceed' : (remainingAfter ?? 0) < 10 ? 'caution' : 'ink'})">
              ${fmtHours(remainingAfter ?? 0)} h left
            </b>
          </div>`
        : ''}
    `;

    submit.disabled = a.status === 'GROUNDED';
  };

  const refreshDefect = () => {
    const on = $<HTMLInputElement>('#cap-hasdefect').checked;
    $('#cap-defect').style.display = on ? 'flex' : 'none';
    if (!on) return;

    const sev = $<HTMLSelectElement>('#cap-defsev').value;
    $('#cap-melwrap').style.display = sev === 'DEFERRABLE' ? 'flex' : 'none';
    const note = $('#cap-defnote');

    if (sev === 'GROUNDING') {
      note.innerHTML = html`<div class="callout c-exceed"><span class="cg">✕</span><div>
        <b>The aircraft will be grounded as soon as this is filed.</b> It will not be available for
        another flight, and no certificate can be issued until the defect is cleared.
      </div></div>`;
    } else if (sev === 'ADVISORY') {
      note.innerHTML = html`<div class="callout c-info"><span class="cg">i</span><div>
        Advisory items do not limit dispatch. They still count towards the defect rate and
        recurring-defect detection.
      </div></div>`;
    } else {
      const days = { A: null, B: 3, C: 10, D: 120 }[$<HTMLSelectElement>('#cap-melcat').value] as number | null;
      const limit = days !== null ? new Date(Date.now() + days * 86_400_000) : null;
      note.innerHTML = html`<div class="callout c-caution"><span class="cg">!</span><div>
        Rectification limit <b>${limit ? esc(fmtDate(limit.toISOString())) : 'as stated in the MEL entry'}</b>${days ? ` (${days} days)` : ''}.
        When the limit passes without rectification the item becomes a grounding defect on its own.
      </div></div>`;
    }
  };

  acSelect.addEventListener('change', refresh);
  ['#cap-off', '#cap-on'].forEach((s) =>
    $<HTMLInputElement>(s).addEventListener('change', () => { recalcBlock(); refresh(); }),
  );
  ['#cap-block', '#cap-ldg'].forEach((s) =>
    $<HTMLInputElement>(s).addEventListener('input', refresh),
  );
  $<HTMLInputElement>('#cap-hasdefect').addEventListener('change', refreshDefect);
  $<HTMLSelectElement>('#cap-defsev').addEventListener('change', refreshDefect);
  $<HTMLSelectElement>('#cap-melcat').addEventListener('change', refreshDefect);

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const errorBox = $('#cap-error');
    errorBox.innerHTML = '';
    submit.disabled = true;
    submit.textContent = 'Filing…';
    $('#cap-state').textContent = 'FILING';

    const numOrUndef = (sel: string) => {
      const v = $<HTMLInputElement>(sel).value.trim();
      return v === '' ? undefined : Number(v);
    };

    /**
     * Said here rather than left to the server.
     *
     * An unreadable block time comes back from the API as a validation
     * message about a field the pilot cannot see the name of, on a ramp, with
     * the engine running. The box is on screen and the fix is in it.
     */
    const blockHours = parseHours($<HTMLInputElement>('#cap-block').value);
    if (blockHours === undefined) {
      errorBox.innerHTML = html`<div class="callout c-exceed"><span class="cg">✕</span><div>
        Block time has to be a number of hours — <b>${esc(hoursPlaceholder())}</b>. Either convention is
        read, so 1.5 and 1:30 both mean an hour and a half.
      </div></div>`;
      submit.disabled = false;
      submit.textContent = 'Sign & file entry';
      $('#cap-state').textContent = 'ERROR';
      return;
    }

    const hasDefect = $<HTMLInputElement>('#cap-hasdefect').checked;
    const severity = $<HTMLSelectElement>('#cap-defsev').value as 'GROUNDING' | 'DEFERRABLE' | 'ADVISORY';

    const body: RecordFlight = {
      aircraftId: acSelect.value,
      flightDate: $<HTMLInputElement>('#cap-date').value,
      departure: $<HTMLInputElement>('#cap-from').value || undefined,
      arrival: $<HTMLInputElement>('#cap-to').value || undefined,
      offBlocks: $<HTMLInputElement>('#cap-off').value || undefined,
      onBlocks: $<HTMLInputElement>('#cap-on').value || undefined,
      blockHours,
      landings: Number($<HTMLInputElement>('#cap-ldg').value),
      fuelUpliftL: numOrUndef('#cap-fuel'),
      oilUpliftL: numOrUndef('#cap-oil'),
      natureOfFlight: $<HTMLSelectElement>('#cap-nature').value,
      clientEntryId: entryKey,
      hasDefect,
      defect: hasDefect
        ? {
            description: $<HTMLTextAreaElement>('#cap-defdesc').value,
            severity,
            melCategory: severity === 'DEFERRABLE'
              ? ($<HTMLSelectElement>('#cap-melcat').value as 'A' | 'B' | 'C' | 'D')
              : undefined,
          }
        : undefined,
    };

    try {
      const result = await api.techlog.record(body);
      if (result.aircraft) {
        const idx = ctx.aircraft.findIndex((a) => a.id === result.aircraft!.id);
        if (idx >= 0) ctx.aircraft[idx] = result.aircraft;
      }
      // A new key, so the next entry is a new flight rather than a replay.
      entryKey = newEntryKey();
      $('#cap-state').textContent = 'FILED';
      toast(
        result.aircraft
          ? `Filed — ${result.aircraft.registration} now at ${fmtHours(result.aircraft.tsnHours)} h`
          : 'Entry filed',
      );
      onFiled();
    } catch (err) {
      $('#cap-state').textContent = 'ERROR';
      const message =
        err instanceof ApiError
          ? err.fieldMessages.join(' ') || err.message
          : 'Could not file the entry.';
      errorBox.innerHTML = html`<div class="callout c-exceed"><span class="cg">✕</span><div>${esc(message)}</div></div>`;
    } finally {
      submit.disabled = false;
      submit.textContent = 'Sign & file entry';
      refresh();
    }
  });

  recalcBlock();
  refresh();
  refreshDefect();
}

// ── view ────────────────────────────────────────────────────────────────────

export async function renderTechlog(host: HTMLElement): Promise<void> {
  host.innerHTML = html`
    <div class="phead"><div><h1>Tech log</h1><p>Loading…</p></div></div>
    ${loading('Reading recent flights')}
  `;

  let entries: TechlogEntry[];
  let ctx: TechlogContext;
  try {
    [entries, ctx] = await Promise.all([api.techlog.recent(), api.techlog.context()]);
  } catch (e) {
    host.innerHTML = errorCard(e instanceof Error ? e.message : 'Could not load the tech log.', 'reload');
    host.querySelector('[data-action=reload]')?.addEventListener('click', () => renderTechlog(host));
    return;
  }

  const draw = () => {
    const hours = entries.slice(0, 30).reduce((n, f) => n + f.blockHours, 0);
    host.innerHTML = html`
      <div class="phead">
        <div>
          <h1>Tech log</h1>
          <p>Pilot capture on the ramp, straight into the due list — no re-keying</p>
        </div>
      </div>

      <div class="tiles">
        <div class="tile t-acc"><span class="lbl">Entries shown</span><b>${fmtInt(entries.length)}</b><span class="sub">most recent first</span></div>
        <div class="tile t-acc"><span class="lbl">Hours, last 30 entries</span><b>${fmtHours(hours)}</b><span class="sub">block time</span></div>
        <div class="tile ${entries.some((e) => e.defect?.severity === 'GROUNDING') ? 't-exceed' : 't-ok'}">
          <span class="lbl">Defects reported</span>
          <b>${entries.filter((e) => e.defect).length}</b>
          <span class="sub">on the entries shown</span>
        </div>
        <div class="tile ${ctx.aircraft.some((a) => a.status === 'GROUNDED') ? 't-exceed' : 't-ok'}">
          <span class="lbl">Grounded</span>
          <b>${ctx.aircraft.filter((a) => a.status === 'GROUNDED').length}</b>
          <span class="sub">cannot accept an entry</span>
        </div>
      </div>

      <div class="cols">
        ${logCard(entries)}
        ${captureCard(ctx)}
      </div>
    `;

    bindCapture(host, ctx, async () => {
      entries = await api.techlog.recent();
      draw();
    });
  };

  draw();
}
